import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import arthereumProvider from "../../backend/ArthereumProvider";
import { UserModelData } from "../../types";
import { actions } from "../redux";
import getUserSignInData from "../utils/getUserSignInData";
import {
  LOCAL_STORAGE_DATA,
  LocalStorageController,
} from "../utils/LocalStorageController";

export type EnsureUserStatus = "checking" | "authenticated" | "guest" | "error";

/**
 * One silent sign-in shared by every route. Previously Entry, AuthRoute and
 * HomePage each ran their own `signIn` on mount (3 Firestore round-trips per
 * reload); this dedupes them behind a single in-flight promise.
 */
let inFlight: Promise<UserModelData | null> | null = null;

async function silentSignIn(): Promise<UserModelData | null> {
  const initialized = LocalStorageController.connect().getData(
    LOCAL_STORAGE_DATA.SYSTEM_FULLY_INITIALIZED,
    "string"
  );
  if (initialized !== "true") return null;

  const credentials = await getUserSignInData();
  const user = await arthereumProvider.signIn(credentials);
  LocalStorageController.connect().storeData(
    LOCAL_STORAGE_DATA.SIGN_IN_PATH,
    user?.user?.signUpMethod
  );
  return user;
}

export default function useEnsureUser(): {
  status: EnsureUserStatus;
  user: UserModelData | undefined;
  retry: () => void;
} {
  const dispatch = useDispatch();
  const userData: UserModelData | undefined = useSelector(
    (state: any) => state?.actualUser
  );
  const alreadyAuthenticated = !!userData?.user?.id;
  const [status, setStatus] = useState<EnsureUserStatus>(
    alreadyAuthenticated ? "authenticated" : "checking"
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (alreadyAuthenticated) {
      setStatus("authenticated");
      return;
    }
    let cancelled = false;
    setStatus("checking");

    if (!inFlight) {
      inFlight = silentSignIn().finally(() => {
        inFlight = null;
      });
    }

    inFlight
      .then((user) => {
        if (cancelled) return;
        if (user) {
          dispatch(actions.setActualUser(user));
          setStatus("authenticated");
        } else {
          setStatus("guest");
        }
      })
      .catch((error: any) => {
        if (cancelled) return;
        if (error?.message === "Invalid credentials") {
          setStatus("guest");
        } else {
          setStatus("error");
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyAuthenticated, attempt]);

  return {
    status,
    user: userData,
    retry: () => setAttempt((n) => n + 1),
  };
}
